
import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { ChartContainer, ChartTooltipContent } from "@/components/ui/chart";
import { supabase } from "@/integrations/supabase/client";
import { TrendingDown, TrendingUp, CreditCard, Receipt, BarChart4 } from "lucide-react";

interface RevenueData {
  date: string;
  revenue: number;
}

export const RevenueMetrics = () => {
  const [revenueData, setRevenueData] = useState<RevenueData[]>([]);
  const [totalRevenue, setTotalRevenue] = useState<number>(0);
  const [revenueGrowth, setRevenueGrowth] = useState<{ percentage: number; positive: boolean }>({ percentage: 0, positive: true });
  const [avgRevenuePerBooking, setAvgRevenuePerBooking] = useState<number>(0);
  const [timeRange, setTimeRange] = useState<number>(30);

  useEffect(() => {
    fetchRevenueData();
    fetchRevenueMetrics();
  }, [timeRange]);

  const fetchRevenueData = async () => {
    try {
      // Use our new RPC function for daily revenue
      const { data, error } = await supabase.rpc('get_revenue_by_day', { days_range: timeRange });
      
      if (error) throw error;
      
      // Data comes ready to use from our function
      setRevenueData(data || []);
    } catch (error) {
      console.error('Error fetching revenue data:', error);
    }
  };

  const fetchRevenueMetrics = async () => {
    try {
      // Fetch all metrics in parallel using our new RPC functions
      const [
        { data: totalRevenueData, error: totalRevenueError },
        { data: revenueGrowthData, error: revenueGrowthError },
        { data: avgRevenueData, error: avgRevenueError },
      ] = await Promise.all([
        supabase.rpc('get_total_revenue'),
        supabase.rpc('get_revenue_growth'),
        supabase.rpc('get_avg_revenue_per_booking'),
      ]);
      
      if (totalRevenueError) throw totalRevenueError;
      if (revenueGrowthError) throw revenueGrowthError;
      if (avgRevenueError) throw avgRevenueError;
      
      // Set total revenue
      setTotalRevenue(totalRevenueData?.[0]?.total_revenue || 0);
      
      // Set revenue growth
      setRevenueGrowth({
        percentage: revenueGrowthData?.[0]?.growth_percentage || 0,
        positive: revenueGrowthData?.[0]?.is_positive || true
      });
      
      // Set average revenue per booking
      setAvgRevenuePerBooking(avgRevenueData?.[0]?.avg_revenue || 0);
    } catch (error) {
      console.error('Error fetching revenue metrics:', error);
    }
  };

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold">Revenue Analytics</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Total Revenue
            </CardTitle>
            <CreditCard className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">₹{totalRevenue.toFixed(2)}</div>
            <p className="text-xs text-muted-foreground">
              From all bookings
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Revenue Growth
            </CardTitle>
            {revenueGrowth.positive ? (
              <TrendingUp className="h-4 w-4 text-green-500" />
            ) : (
              <TrendingDown className="h-4 w-4 text-red-500" />
            )}
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold ${revenueGrowth.positive ? 'text-green-500' : 'text-red-500'}`}>
              {revenueGrowth.percentage.toFixed(1)}%
            </div>
            <p className="text-xs text-muted-foreground">
              Last 30 days vs. previous
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Avg. Revenue / Booking
            </CardTitle>
            <Receipt className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">₹{avgRevenuePerBooking.toFixed(2)}</div>
            <p className="text-xs text-muted-foreground">
              Per completed booking
            </p>
          </CardContent>
        </Card>
      </div>
      
      <Card>
        <CardHeader>
          <CardTitle>Revenue Trend</CardTitle>
          <CardDescription>Daily revenue over the last {timeRange} days</CardDescription>
        </CardHeader>
        <CardContent className="pt-2">
          <ChartContainer 
            config={{ 
              "revenue": { theme: { light: "#16a34a", dark: "#22c55e" } }
            }} 
            className="h-[300px]"
          >
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={revenueData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip content={<ChartTooltipContent />} />
                <Line
                  type="monotone"
                  dataKey="revenue"
                  name="Revenue"
                  stroke="var(--color-revenue)"
                  activeDot={{ r: 8 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </ChartContainer>
        </CardContent>
      </Card>
    </div>
  );
};
